import type { SupabaseClient } from '@ims/remote'

// Client for the api Worker (apps/api). Online only.

/** A product's details from an open product database (Open Food Facts and its sisters). */
export interface BarcodeSuggestion {
  name: string
  brand: string | null
  /** Pack size as printed, like "400 g". */
  quantity: string | null
  /** The database it came from, shown as the attribution its licence asks for. */
  source: string
}

/** The name to suggest for a product: its name with the pack size, unless that is in it already. */
export function suggestedName(suggestion: Pick<BarcodeSuggestion, 'name' | 'quantity'>): string {
  const { name, quantity } = suggestion
  if (!quantity) return name
  const squeeze = (text: string) => text.toLowerCase().replace(/\s+/g, '')
  return squeeze(name).includes(squeeze(quantity)) ? name : `${name} ${quantity}`
}

export class ApiRequestError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
  ) {
    super(message)
  }
}

export function createApi(apiUrl: string, supabase: SupabaseClient) {
  async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const { data } = await supabase.auth.getSession()
    const token = data.session?.access_token
    if (!token) throw new ApiRequestError('unauthenticated', 'Sign in again', 401)
    let response: Response
    try {
      response = await fetch(`${apiUrl}${path}`, {
        ...init,
        headers: { Authorization: `Bearer ${token}`, ...(init.headers ?? {}) },
      })
    } catch {
      throw new ApiRequestError('network', 'You are offline. This needs an internet connection.', 0)
    }
    if (response.status === 204) return undefined as T
    const body = (await response.json().catch(() => ({}))) as {
      error?: { code: string; message: string }
    }
    if (!response.ok) {
      throw new ApiRequestError(
        body.error?.code ?? 'error',
        body.error?.message ?? `Request failed (${response.status})`,
        response.status,
      )
    }
    return body as T
  }
  const json = (method: string, body: unknown): RequestInit => ({
    method,
    body: JSON.stringify(body),
    headers: { 'Content-Type': 'application/json' },
  })

  return {
    billingProviders: () => request<{ khalti: boolean; esewa: boolean }>('/v1/billing/providers'),
    /** Asks for the welcome email of a business just created; the server sends it once. */
    /** The day report for today so far, as HTML; nothing is sent (managers and up). */
    dayReportSample: (tenantId: string) =>
      request<{ subject: string; html: string; hasSales: boolean }>(
        `/v1/tenants/${tenantId}/day-report/sample`,
        { method: 'POST' },
      ),
    welcome: (tenantId: string) =>
      request<{ sent: boolean }>(`/v1/tenants/${tenantId}/welcome`, { method: 'POST' }),
    checkout: (
      tenantId: string,
      planId: string,
      period: 'month' | 'half_year' | 'year',
      provider: 'khalti' | 'esewa',
    ) =>
      request<
        | { kind: 'redirect'; url: string; paymentId: string }
        | { kind: 'form'; action: string; fields: Record<string, string>; paymentId: string }
      >(`/v1/tenants/${tenantId}/billing/checkout`, json('POST', { planId, period, provider })),
    invite: (
      tenantId: string,
      email: string,
      role: 'viewer' | 'waiter' | 'staff' | 'manager' | 'admin',
    ) =>
      request<{ inviteId: string; emailed: boolean; link?: string }>(
        '/v1/invites',
        json('POST', { tenantId, email, role }),
      ),
    shareInvoice: (tenantId: string, invoiceId: string) =>
      request<{ url: string }>(`/v1/tenants/${tenantId}/invoices/${invoiceId}/share`, {
        method: 'POST',
      }),
    revokeShare: (tenantId: string, invoiceId: string) =>
      request<void>(`/v1/tenants/${tenantId}/invoices/${invoiceId}/share`, { method: 'DELETE' }),
    uploadImage: (tenantId: string, file: File) =>
      request<{ path: string }>(`/v1/tenants/${tenantId}/images`, {
        method: 'POST',
        body: file,
        headers: { 'Content-Type': file.type },
      }),
    /** What the open product databases say a barcode is, or null (a suggestion to check). */
    lookupBarcode: (tenantId: string, code: string) =>
      request<{ suggestion: BarcodeSuggestion | null }>(
        `/v1/tenants/${tenantId}/barcodes/${encodeURIComponent(code)}`,
      ),
    /** Pushes what is due to phones now (after food is marked ready); the cron does it anyway. */
    pushKick: (tenantId: string) =>
      request<{ alerts: number; sent: number }>(`/v1/tenants/${tenantId}/push/kick`, {
        method: 'POST',
      }),
    /** "Send me a test alert": this person's devices on this business. */
    pushTest: (tenantId: string) =>
      request<{ sent: number }>(`/v1/tenants/${tenantId}/push/test`, { method: 'POST' }),
    signImages: (tenantId: string, paths: string[]) =>
      request<{ urls: Record<string, string>; expiresAt: number }>(
        `/v1/tenants/${tenantId}/images/sign`,
        json('POST', { paths }),
      ),
  }
}

export type Api = ReturnType<typeof createApi>
